/**
 * PasskeyLoginButton Component
 *
 * Compact button for passkey login - used in signin page
 * Progressive enhancement pattern: shows only if passkeys are enabled
 */

'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { usePasskey } from '@/hooks/usePasskey';
import { Fingerprint, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PasskeyLoginButton({ onSuccess, className = '' }) {
  const router = useRouter();
  const { loginWithPasskey, isPasskeySupported, isLoading, error } = usePasskey();
  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);

  const handlePasskeyLogin = async () => {
    // If email is known from form, use it. Otherwise prompt for email.
    if (!email) {
      setShowEmailInput(true);
      return;
    }

    try {
      const result = await loginWithPasskey(email);
      if (result?.success) {
        if (onSuccess) {
          onSuccess();
        } else {
          router.push('/dashboard');
        }
      }
    } catch (err) {
      console.error('Passkey login failed:', err);
    }
  };

  // Hide if browser doesn't support WebAuthn
  if (!isPasskeySupported()) {
    return null;
  }

  if (showEmailInput) {
    return (
      <div className={`space-y-2 ${className}`}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-Mail für Passkey-Login"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          autoFocus
        />
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowEmailInput(false)}
            className="flex-1"
          >
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={handlePasskeyLogin}
            disabled={isLoading || !email}
            className="flex-1"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Lädt...
              </>
            ) : (
              <>
                <Fingerprint className="mr-2 h-4 w-4" />
                Anmelden
              </>
            )}
          </Button>
        </div>
        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="lg"
      onClick={handlePasskeyLogin}
      disabled={isLoading}
      className={`w-full ${className}`}
    >
      {isLoading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Authentifizierung läuft...
        </>
      ) : (
        <>
          <Fingerprint className="mr-2 h-5 w-5" />
          Mit Passkey anmelden
        </>
      )}
    </Button>
  );
}
